const HIGHLIGHT_TAG = 'mark';
const HIGHLIGHT_ATTR = 'data-docx-search-hit';
const HIGHLIGHT_ACTIVE_ATTR = 'data-docx-search-active';

const escapeRegExp = (input: string): string =>
  input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const isSkippableNode = (node: Node): boolean => {
  if (node.nodeType !== Node.ELEMENT_NODE) return false;
  const tag = (node as Element).tagName;
  return (
    tag === 'SCRIPT' || tag === 'STYLE' || tag === 'MARK' || tag === 'NOSCRIPT'
  );
};

const collectTextNodes = (root: HTMLElement): Text[] => {
  const out: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      const parent = node.parentNode;
      if (!parent) return NodeFilter.FILTER_REJECT;
      if (isSkippableNode(parent)) return NodeFilter.FILTER_REJECT;
      if (!node.nodeValue || !node.nodeValue.trim())
        return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let current = walker.nextNode();
  while (current) {
    out.push(current as Text);
    current = walker.nextNode();
  }
  return out;
};

export const clearHighlights = (root: HTMLElement | null): void => {
  if (!root) return;
  const marks = root.querySelectorAll<HTMLElement>(
    `${HIGHLIGHT_TAG}[${HIGHLIGHT_ATTR}]`
  );
  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;
    while (mark.firstChild) parent.insertBefore(mark.firstChild, mark);
    parent.removeChild(mark);
    parent.normalize();
  });
};

export const applyHighlights = (
  root: HTMLElement | null,
  query: string
): HTMLElement[] => {
  if (!root) return [];
  clearHighlights(root);
  if (!query) return [];
  const pattern = new RegExp(escapeRegExp(query), 'gi');
  const matches: HTMLElement[] = [];

  const textNodes = collectTextNodes(root);
  textNodes.forEach((textNode) => {
    const value = textNode.nodeValue ?? '';
    pattern.lastIndex = 0;
    const hits: { start: number; end: number }[] = [];
    let m: RegExpExecArray | null = pattern.exec(value);
    while (m) {
      if (m[0].length === 0) {
        pattern.lastIndex += 1;
      } else {
        hits.push({ start: m.index, end: m.index + m[0].length });
      }
      m = pattern.exec(value);
    }
    if (hits.length === 0) return;

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    hits.forEach(({ start, end }) => {
      if (start > cursor) {
        fragment.appendChild(
          document.createTextNode(value.slice(cursor, start))
        );
      }
      const mark = document.createElement(HIGHLIGHT_TAG);
      mark.setAttribute(HIGHLIGHT_ATTR, '');
      mark.style.backgroundColor = '#FFE58A';
      mark.style.color = 'inherit';
      mark.style.padding = '0';
      mark.textContent = value.slice(start, end);
      fragment.appendChild(mark);
      matches.push(mark);
      cursor = end;
    });
    if (cursor < value.length) {
      fragment.appendChild(document.createTextNode(value.slice(cursor)));
    }
    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return matches;
};

export const setActiveHighlight = (
  matches: HTMLElement[],
  index: number
): void => {
  matches.forEach((node, i) => {
    if (i === index) {
      node.setAttribute(HIGHLIGHT_ACTIVE_ATTR, '');
      node.style.backgroundColor = '#FFB020';
      if (typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    } else {
      node.removeAttribute(HIGHLIGHT_ACTIVE_ATTR);
      node.style.backgroundColor = '#FFE58A';
    }
  });
};
